<template>
  <div class="analytics-readonly-panel">
    <!-- Loading State -->
    <v-skeleton-loader
      v-if="loading"
      type="card"
      height="400"
    ></v-skeleton-loader>

    <!-- Analytics Content -->
    <div v-else>
      <!-- Summary Cards -->
      <v-row class="mb-4">
        <v-col cols="6" md="3" v-for="metric in summaryMetrics" :key="metric.key">
          <v-card class="h-100" elevation="2">
            <v-card-text class="text-center">
              <v-icon 
                :color="metric.color" 
                size="32" 
                class="mb-2"
              >
                {{ metric.icon }}
              </v-icon>
              <h3 class="text-h5 font-weight-bold">{{ metric.value }}</h3>
              <p class="text-caption">{{ metric.label }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-row v-if="charts && charts.length > 0">
        <v-col 
          v-for="chart in availableCharts" 
          :key="chart.type"
          :cols="chart.cols || 12"
          :md="chart.md || 6"
        >
          <v-card elevation="2">
            <v-card-title>
              <v-icon :color="chart.color" class="mr-2">{{ chart.icon }}</v-icon>
              {{ chart.title }}
            </v-card-title>
            <v-card-text>
              <div :id="`chart-${chart.type}`" style="height: 300px;"></div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Export Section -->
      <v-row v-if="exportEnabled" class="mt-4">
        <v-col cols="12">
          <v-card elevation="2">
            <v-card-title>
              <v-icon color="primary" class="mr-2">mdi-download</v-icon>
              Exportar Datos
            </v-card-title>
            <v-card-text>
              <v-btn
                color="primary"
                @click="$emit('export')"
                :loading="exporting"
              >
                <v-icon start>mdi-file-export</v-icon>
                Exportar Analytics
              </v-btn>
              <v-chip color="info" class="ml-2" size="small">
                Solo datos permitidos
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- No Data State -->
      <v-row v-if="!data || Object.keys(data).length === 0">
        <v-col cols="12">
          <v-card elevation="2">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey">mdi-chart-line-variant</v-icon>
              <h3 class="text-h6 mt-4">No hay datos disponibles</h3>
              <p class="text-body-2 text-grey">
                Los datos de analytics se cargarán automáticamente cuando estén disponibles.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'

interface Props {
  data: any
  charts: string[]
  exportEnabled: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const emit = defineEmits<{
  export: []
}>()

const exporting = ref(false)

// Summary metrics computed from data
const summaryMetrics = computed(() => {
  if (!props.data || Object.keys(props.data).length === 0) {
    return []
  }

  return [
    {
      key: 'total',
      label: 'Total Registros',
      value: props.data.totalRecords || 0,
      icon: 'mdi-database',
      color: 'primary'
    },
    {
      key: 'active',
      label: 'Activos',
      value: props.data.activeRecords || 0,
      icon: 'mdi-check-circle',
      color: 'success'
    },
    {
      key: 'pending',
      label: 'Pendientes',
      value: props.data.pendingRecords || 0,
      icon: 'mdi-clock',
      color: 'warning'
    },
    {
      key: 'issues',
      label: 'Con Problemas',
      value: props.data.issuesCount || 0,
      icon: 'mdi-alert-circle',
      color: 'error'
    }
  ]
})

// Available charts based on permissions
const availableCharts = computed(() => {
  const chartConfigs = {
    trends: {
      type: 'trends',
      title: 'Tendencias',
      icon: 'mdi-chart-line',
      color: 'blue',
      cols: 12,
      md: 8
    },
    distribution: {
      type: 'distribution',
      title: 'Distribución',
      icon: 'mdi-chart-pie',
      color: 'green',
      cols: 12,
      md: 4
    },
    performance: {
      type: 'performance',
      title: 'Rendimiento',
      icon: 'mdi-speedometer',
      color: 'orange',
      cols: 12,
      md: 6
    }
  }

  return props.charts
    .filter(chart => chartConfigs[chart])
    .map(chart => chartConfigs[chart])
})

// Initialize charts when data changes
const initializeCharts = () => {
  // Placeholder for chart initialization
  // In a real implementation, this would integrate with Chart.js or similar
  console.log('🔄 Inicializando charts para analista:', props.charts)
  console.log('📊 Datos disponibles:', props.data)
}

watch(() => props.data, initializeCharts, { deep: true })

onMounted(() => {
  if (props.data && Object.keys(props.data).length > 0) {
    initializeCharts()
  }
})
</script>

<style scoped>
.analytics-readonly-panel {
  width: 100%;
}

.v-card {
  border-radius: 8px;
}

.v-card:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease;
}
</style>
